import React, { useState } from "react";
import { CreditCard, Plus, Calendar, AlertCircle, CheckCircle2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { endpoints } from "../lib/api";
import { toast } from "sonner";

const STATUS_META = {
  paid:     { label: "Paid",     cls: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900/50" },
  partial:  { label: "Partial",  cls: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900/50" },
  pending:  { label: "Pending",  cls: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-950/30 dark:text-slate-300 dark:border-slate-900/50" },
  overdue:  { label: "Overdue",  cls: "bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-900/50" },
};

function formatDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "-"; }
}

export default function PaymentTracker({ member, currency = "₹", onChanged }) {
  const status = member.payment_status || "paid";
  const statusMeta = STATUS_META[status] || STATUS_META.paid;
  const total = member.total_fee ?? member.plan_amount ?? 0;
  const paid = member.paid_amount ?? 0;
  const remaining = member.remaining_amount ?? Math.max(0, total - paid);
  const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 100;

  const [addOpen, setAddOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);

  return (
    <section data-testid="payment-tracker" className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-display text-base font-semibold text-foreground">Payment</h3>
          <span data-testid={`payment-status-${status}`} className={`ml-1 inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md border ${statusMeta.cls}`}>
            {statusMeta.label}
          </span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPlanOpen(true)} data-testid="open-payment-plan-btn"
            className="text-xs font-medium px-2.5 py-1.5 rounded-md border border-border bg-card hover:bg-muted text-muted-foreground">
            Plan
          </button>
          <button onClick={() => setAddOpen(true)} data-testid="open-add-payment-btn"
            className="text-xs font-medium px-2.5 py-1.5 rounded-md bg-primary text-primary-foreground hover:opacity-90 inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add payment
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <Stat label="Total fee"   value={`${currency}${(total||0).toLocaleString("en-IN")}`} />
        <Stat label="Paid"        value={`${currency}${(paid||0).toLocaleString("en-IN")}`}  testid="paid-amount-stat" />
        <Stat label="Remaining"   value={`${currency}${(remaining||0).toLocaleString("en-IN")}`} testid="remaining-stat" tone={remaining > 0 ? "warning" : "success"} />
      </div>

      <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-3" data-testid="payment-progress">
        <div
          className={`h-full ${
            status === "paid" ? "bg-emerald-500" :
            status === "partial" ? "bg-amber-500" :
            status === "overdue" ? "bg-rose-500" : "bg-slate-400"
          }`}
          style={{ width: `${pct}%`, transition: "width 0.4s ease-out" }}
        />
      </div>

      {member.due_date && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Calendar className="w-3 h-3" />
          Due: <span className={`font-medium ${status === "overdue" ? "text-rose-600 dark:text-rose-400" : "text-foreground"}`}>{formatDate(member.due_date)}</span>
          {status === "overdue" && (
            <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400 font-medium">
              <AlertCircle className="w-3 h-3" /> overdue
            </span>
          )}
        </div>
      )}

      {/* Recent payments */}
      {(member.payments || []).length > 0 && (
        <div className="mt-4 pt-3 border-t border-border">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-2">Recent payments</p>
          <ul className="space-y-1.5" data-testid="payment-history-list">
            {(member.payments || []).slice(-5).reverse().map(p => (
              <li key={p.id} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                  <span className="text-foreground font-medium">{currency}{(p.amount||0).toLocaleString("en-IN")}</span>
                  <span className="text-muted-foreground">· {p.method}</span>
                  {p.note && <span className="text-muted-foreground/70">· {p.note}</span>}
                </div>
                <span className="text-muted-foreground/70">{formatDate(p.paid_on)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <AddPaymentDialog open={addOpen} onClose={() => setAddOpen(false)} memberId={member.id} onChanged={onChanged} />
      <PaymentPlanDialog open={planOpen} onClose={() => setPlanOpen(false)} member={member} onChanged={onChanged} />
    </section>
  );
}

function Stat({ label, value, tone = "default", testid }) {
  const toneCls = tone === "success"
    ? "text-emerald-700 dark:text-emerald-400"
    : tone === "warning"
    ? "text-amber-700 dark:text-amber-400"
    : "text-foreground";
  return (
    <div data-testid={testid} className="rounded-lg bg-muted/40 px-3 py-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1">{label}</p>
      <p className={`text-base font-bold tabular-nums ${toneCls}`}>{value}</p>
    </div>
  );
}

function AddPaymentDialog({ open, onClose, memberId, onChanged }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("upi");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) { toast.error("Amount required"); return; }
    setBusy(true);
    try {
      await endpoints.addPayment(memberId, { amount: parseFloat(amount), method, note });
      toast.success("Payment recorded");
      setAmount(""); setNote("");
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add payment");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-payment-dialog" className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-display">Add payment</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Amount (₹)</Label>
            <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-amount-input" required />
          </div>
          <div>
            <Label className="text-xs">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["cash", "upi", "card", "bank_transfer", "other"].map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="payment-note-input" placeholder="Renewal / partial / etc." />
          </div>
          <DialogFooter className="gap-2">
            <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 text-sm rounded-md border border-border bg-card hover:bg-muted">Cancel</button>
            <button type="submit" disabled={busy} data-testid="add-payment-submit"
              className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground font-medium hover:opacity-90">
              Save payment
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PaymentPlanDialog({ open, onClose, member, onChanged }) {
  const [totalFee, setTotalFee] = useState(member.total_fee ?? member.plan_amount ?? "");
  const [dueDate, setDueDate] = useState(
    member.due_date ? new Date(member.due_date).toISOString().slice(0, 10) : ""
  );
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    setTotalFee(member.total_fee ?? member.plan_amount ?? "");
    setDueDate(member.due_date ? new Date(member.due_date).toISOString().slice(0, 10) : "");
  }, [member]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await endpoints.setPaymentPlan(member.id, {
        total_fee: totalFee ? parseFloat(totalFee) : null,
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
      });
      toast.success("Payment plan updated");
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update plan");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payment-plan-dialog" className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-display">Payment plan</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Total fee (₹)</Label>
            <Input type="number" min="0" step="0.01" value={totalFee} onChange={(e) => setTotalFee(e.target.value)} data-testid="plan-total-input" />
          </div>
          <div>
            <Label className="text-xs">Due date</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="plan-due-date-input" />
          </div>
          <DialogFooter className="gap-2">
            <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 text-sm rounded-md border border-border bg-card hover:bg-muted">Cancel</button>
            <button type="submit" disabled={busy} data-testid="plan-submit"
              className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground font-medium hover:opacity-90">
              Save plan
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
